import React, { useState, useEffect, useCallback } from "react";
import MaterialTable from "material-table";
import { makeStyles } from "@mui/styles";
import {
    Typography,
    Chip,
    Avatar,
    Box,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Card,
    CardContent,
    Grid,
    IconButton,
    Modal,
    Button as MuiButton,
    CircularProgress,
    Alert,
    Snackbar,
} from "@mui/material";
import { colors } from "../components/Theme/WebTheme";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GetAppIcon from "@mui/icons-material/GetApp";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import BlockIcon from "@mui/icons-material/Block";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CancelIcon from "@mui/icons-material/Cancel";
import Button from "components/CustomButtons/Button.js";
import { useTranslation } from "react-i18next";
import SubscriptionAPI from "../common/subscriptionApi";
import {
    getStatusColor,
    getStatusBackgroundColor,
    getStatusTextColor,
    formatStatus,
    formatPaymentMethod,
    formatAdminNote,
} from "../common/subscriptionUtils";
import { downloadCsv } from "../common/sharedFunctions";

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
    },
    filterContainer: {
        display: "flex",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        flexWrap: "wrap",
    },
    filterItem: {
        minWidth: 200,
    },
    summaryCard: {
        padding: theme.spacing(2),
        backgroundColor: "#f5f5f5",
        borderRadius: 8,
        marginBottom: theme.spacing(2),
    },
    summaryText: {
        fontWeight: "bold",
        color: colors.MAIN_COLOR,
    },
    expandedContent: {
        padding: theme.spacing(3),
        backgroundColor: "#fafafa",
    },
    detailTitle: {
        fontWeight: "bold",
        marginBottom: theme.spacing(1),
        color: "#333",
        fontSize: "1rem",
    },
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    modalPaper: {
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        padding: theme.spacing(0),
        borderRadius: 12,
        width: "90%",
        maxWidth: 600,
        maxHeight: "90vh",
        overflow: "auto",
    },
    modalHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: theme.spacing(3),
        borderBottom: "1px solid #e0e0e0",
    },
    modalContent: {
        padding: theme.spacing(3),
    },
    modalFooter: {
        display: "flex",
        justifyContent: "flex-end",
        gap: theme.spacing(1),
        padding: theme.spacing(3),
        borderTop: "1px solid #e0e0e0",
    },
    actionButton: {
        minWidth: "auto",
        padding: "4px 8px",
        margin: "0 2px",
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: 400,
    },
    errorContainer: {
        padding: theme.spacing(2),
    },
    approveButton: {
        backgroundColor: "#4CAF50",
        color: "white",
        "&:hover": {
            backgroundColor: "#45a049",
        },
    },
    rejectButton: {
        backgroundColor: "#f44336",
        color: "white",
        "&:hover": {
            backgroundColor: "#da190b",
        },
    },
    materialTable: {
        "& .MuiTableRow-root": {
            cursor: "pointer",
        },
        "& .MuiTableCell-root": {
            borderBottom: "1px solid rgba(224, 224, 224, 1)",
        },
    },
    formControl: {
        "& .MuiInputLabel-root": {
            backgroundColor: "white",
            padding: "0 4px",
            "&.Mui-focused": {
                backgroundColor: "white",
            },
        },
    },
}));

export default function SubscriptionSubscribers() {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [subscribers, setSubscribers] = useState([]);
    const [filteredSubscribers, setFilteredSubscribers] = useState([]);
    const [filterPlan, setFilterPlan] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterType, setFilterType] = useState("");
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedSubscriber, setSelectedSubscriber] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("info");
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);

    // New state for subscription management
    const [changePlanModalOpen, setChangePlanModalOpen] = useState(false);
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [availablePlans, setAvailablePlans] = useState([]);
    const [selectedNewPlan, setSelectedNewPlan] = useState(null);
    const [cancelReason, setCancelReason] = useState("");
    const [allPlans, setAllPlans] = useState([]); // State untuk menyimpan semua plans dari API
    const [plansLoading, setPlansLoading] = useState(false); // Loading state untuk plans
    const [plansError, setPlansError] = useState(""); // Error state untuk plans

    const currentLanguage = i18n.language || "en";

    // Helper function to calculate endDate from period
    const calculateEndDateFromPeriod = (startDate, period) => {
        if (!startDate || !period) {
            console.log(
                "❌ calculateEndDateFromPeriod: Missing startDate or period",
                { startDate, period }
            );
            return null;
        }

        console.log("🧮 calculateEndDateFromPeriod input:", {
            startDate,
            period,
            startDateType: typeof startDate,
        });

        // Handle different date formats
        let start;
        if (typeof startDate === "string") {
            // Try to parse different date formats
            if (startDate.includes("/")) {
                // Format like "8/2/2025" - convert to ISO format
                const parts = startDate.split("/");
                if (parts.length === 3) {
                    const [month, day, year] = parts;
                    console.log("🔧 Parsing date format MM/DD/YYYY:", {
                        startDate,
                        month,
                        day,
                        year,
                    });
                    start = new Date(
                        parseInt(year),
                        parseInt(month) - 1,
                        parseInt(day)
                    );
                    console.log("🔧 Parsed date result:", {
                        start,
                        startISO: start.toISOString(),
                    });
                } else {
                    start = new Date(startDate);
                }
            } else {
                start = new Date(startDate);
            }
        } else if (typeof startDate === "number") {
            start = new Date(startDate);
        } else {
            start = new Date(startDate);
        }

        if (isNaN(start.getTime())) {
            console.error("❌ calculateEndDateFromPeriod: Invalid startDate", {
                startDate,
                parsedStart: start,
            });
            return null;
        }

        let endDate;

        switch (period) {
            case "daily":
            case "diario":
                endDate = start.getTime() + 24 * 60 * 60 * 1000; // 1 day
                break;
            case "weekly":
            case "semanal":
                endDate = start.getTime() + 7 * 24 * 60 * 60 * 1000; // 7 days
                break;
            case "monthly":
            case "mensual":
            case "month":
                const monthlyDate = new Date(start);
                monthlyDate.setMonth(monthlyDate.getMonth() + 1);
                endDate = monthlyDate.getTime();
                break;
            case "yearly":
            case "anual":
            case "year":
                const yearlyDate = new Date(start);
                yearlyDate.setFullYear(yearlyDate.getFullYear() + 1);
                endDate = yearlyDate.getTime();
                break;
            default:
                console.warn(
                    `Unknown period: ${period}, defaulting to monthly`
                );
                const defaultDate = new Date(start);
                defaultDate.setMonth(defaultDate.getMonth() + 1);
                endDate = defaultDate.getTime();
        }

        console.log("🧮 calculateEndDateFromPeriod result:", {
            startDate: startDate,
            period: period,
            startTimestamp: start.getTime(),
            endTimestamp: endDate,
            endDateISO: new Date(endDate).toISOString(),
            endDateLocal: new Date(endDate).toLocaleString(),
        });

        return endDate;
    };

    const loadSubscribers = useCallback(async () => {
        console.log(`🚀 loadSubscribers started`);
        try {
            setLoading(true);
            setError("");

            // Try to get data directly from Firebase Admin first
            try {
                console.log(
                    "🔄 Attempting to load data from Firebase Admin..."
                );
                const subscriptionsResponse =
                    await SubscriptionAPI.getSubscriptions();

                if (
                    subscriptionsResponse.success &&
                    subscriptionsResponse.subscriptions
                ) {
                    console.log(
                        "📡 Raw subscriptions from Firebase Admin:",
                        subscriptionsResponse.subscriptions
                    );

                    console.log(
                        "📊 Subscription count:",
                        Object.keys(subscriptionsResponse.subscriptions).length
                    );
                    console.log(
                        "📊 First subscription sample:",
                        Object.values(subscriptionsResponse.subscriptions)[0]
                    );

                    const subscriptions = subscriptionsResponse.subscriptions;
                    const subscribersMap = new Map();

                    // Process each subscription
                    Object.entries(subscriptions).forEach(
                        ([subscriptionId, subscriptionData]) => {
                            console.log(
                                `🔍 Processing subscription ${subscriptionId}:`,
                                {
                                    subscriptionData,
                                    endDate: subscriptionData.endDate,
                                    endDateType:
                                        typeof subscriptionData.endDate,
                                    hasEndDate: "endDate" in subscriptionData,
                                    endDateValue: subscriptionData.endDate,
                                    startDate: subscriptionData.startDate,
                                    period: subscriptionData.period,
                                    userId: subscriptionData.userId,
                                    userName: subscriptionData.userName,
                                    userEmail: subscriptionData.userEmail,
                                    fullData: subscriptionData,
                                }
                            );

                            const userId = subscriptionData.userId;

                            // Skip if userId contains invalid characters for Firebase Database
                            if (
                                userId &&
                                (userId.includes(".") ||
                                    userId.includes("#") ||
                                    userId.includes("$") ||
                                    userId.includes("[") ||
                                    userId.includes("]"))
                            ) {
                                console.log(
                                    `⚠️ Skipping subscription ${subscriptionId} - userId contains invalid characters: ${userId}`
                                );
                            } else if (!subscribersMap.has(userId)) {
                                // Calculate endDate from period if not available
                                let calculatedEndDate =
                                    subscriptionData.endDate;

                                // Always calculate endDate if it's missing, null, undefined, 0, or invalid
                                const shouldCalculateEndDate =
                                    !calculatedEndDate ||
                                    calculatedEndDate === 0 ||
                                    calculatedEndDate === null ||
                                    calculatedEndDate === undefined ||
                                    (typeof calculatedEndDate === "number" &&
                                        calculatedEndDate <= 0);

                                if (
                                    shouldCalculateEndDate &&
                                    subscriptionData.startDate &&
                                    subscriptionData.period
                                ) {
                                    calculatedEndDate =
                                        calculateEndDateFromPeriod(
                                            subscriptionData.startDate,
                                            subscriptionData.period
                                        );

                                    console.log(
                                        `🧮 Calculated endDate for ${userId}:`,
                                        {
                                            startDate:
                                                subscriptionData.startDate,
                                            period: subscriptionData.period,
                                            originalEndDate:
                                                subscriptionData.endDate,
                                            calculatedEndDate:
                                                calculatedEndDate,
                                            calculatedEndDateFormatted:
                                                new Date(
                                                    calculatedEndDate
                                                ).toISOString(),
                                            shouldCalculate:
                                                shouldCalculateEndDate,
                                        }
                                    );
                                }

                                // Get user data from users collection if not available in subscription
                                let userName = subscriptionData.userName;
                                let userEmail = subscriptionData.userEmail;
                                let userPhone = subscriptionData.userPhone;
                                let userType = subscriptionData.userType;

                                // If user data is missing or incomplete, try to get from users collection
                                if (
                                    !userName ||
                                    userName === "Unknown User" ||
                                    !userEmail ||
                                    userEmail === ""
                                ) {
                                    console.log(
                                        `🔍 User data incomplete for ${userId}, will fetch from users collection later`
                                    );
                                    // Note: We'll handle user data fetching separately to avoid async issues in forEach
                                }

                                // Try to combine firstName and lastName if available in subscription data
                                if (
                                    subscriptionData.userFirstName ||
                                    subscriptionData.userLastName
                                ) {
                                    if (
                                        subscriptionData.userFirstName &&
                                        subscriptionData.userLastName
                                    ) {
                                        userName = `${subscriptionData.userFirstName} ${subscriptionData.userLastName}`;
                                    } else if (subscriptionData.userFirstName) {
                                        userName =
                                            subscriptionData.userFirstName;
                                    } else if (subscriptionData.userLastName) {
                                        userName =
                                            subscriptionData.userLastName;
                                    }
                                }

                                const newSubscriber = {
                                    id: userId,
                                    name:
                                        userName && userName !== "Unknown User"
                                            ? userName
                                            : t("unknown_user"),
                                    email:
                                        userEmail && userEmail !== "-"
                                            ? userEmail
                                            : "",
                                    phone: userPhone || "",
                                    userType: userType || "customer",
                                    planName:
                                        subscriptionData.planName ||
                                        "Unknown Plan",
                                    price: subscriptionData.price || 0,
                                    period:
                                        subscriptionData.period || "monthly",
                                    status: subscriptionData.status || "active",
                                    paymentMethod:
                                        subscriptionData.paymentMethod ||
                                        "unknown",
                                    startDate: subscriptionData.startDate,
                                    endDate: calculatedEndDate,
                                    tripsUsed: subscriptionData.tripsUsed || 0,
                                    tripLimit: subscriptionData.tripLimit,
                                    usedWalletMoney:
                                        subscriptionData.usedWalletMoney || 0,
                                    payableAmount:
                                        subscriptionData.payableAmount || 0,
                                    autoRenew:
                                        subscriptionData.autoRenew || false,
                                    canRenew:
                                        subscriptionData.canRenew || false,
                                    adminVerified:
                                        subscriptionData.adminVerified || false,
                                    planDeleted:
                                        subscriptionData.planDeleted || false,
                                    transactions: [],
                                };

                                console.log(
                                    `👤 Created new subscriber with endDate:`,
                                    {
                                        userId,
                                        endDate: newSubscriber.endDate,
                                        endDateFromSource:
                                            subscriptionData.endDate,
                                        calculatedEndDate: calculatedEndDate,
                                        endDateType:
                                            typeof newSubscriber.endDate,
                                        endDateIsNull:
                                            newSubscriber.endDate === null,
                                        endDateIsUndefined:
                                            newSubscriber.endDate === undefined,
                                        endDateTruthy: !!newSubscriber.endDate,
                                        startDate: subscriptionData.startDate,
                                        period: subscriptionData.period,
                                    }
                                );

                                // Critical validation
                                if (!newSubscriber.endDate) {
                                    console.error(
                                        "❌ CRITICAL ERROR: endDate is null/undefined after assignment!",
                                        {
                                            originalEndDate:
                                                subscriptionData.endDate,
                                            calculatedEndDate:
                                                calculatedEndDate,
                                            startDate:
                                                subscriptionData.startDate,
                                            period: subscriptionData.period,
                                            calculatedEndDateType:
                                                typeof calculatedEndDate,
                                        }
                                    );
                                }

                                subscribersMap.set(userId, newSubscriber);
                            }

                            // Format subscription data for history display using mobile app format
                            const formattedSubscriptionData = {
                                timestamp: subscriptionData.startDate,
                                type: "subscription_created",
                                note: subscriptionData.adminNote || "",
                                status: subscriptionData.status,
                                amount: subscriptionData.price,
                                planName: subscriptionData.planName,
                                paymentMethod: subscriptionData.paymentMethod,
                            };

                            subscribersMap
                                .get(userId)
                                .transactions.push(formattedSubscriptionData);

                            // Update with most recent subscription data
                            const subscriber = subscribersMap.get(userId);
                            if (
                                new Date(subscriptionData.startDate) >
                                new Date(subscriber.startDate)
                            ) {
                                // Calculate endDate from period if not available for update
                                let updateEndDate = subscriptionData.endDate;

                                // Always calculate endDate if it's missing, null, undefined, 0, or invalid
                                const shouldCalculateUpdateEndDate =
                                    !updateEndDate ||
                                    updateEndDate === 0 ||
                                    updateEndDate === null ||
                                    updateEndDate === undefined ||
                                    (typeof updateEndDate === "number" &&
                                        updateEndDate <= 0);

                                if (
                                    shouldCalculateUpdateEndDate &&
                                    subscriptionData.startDate &&
                                    subscriptionData.period
                                ) {
                                    updateEndDate = calculateEndDateFromPeriod(
                                        subscriptionData.startDate,
                                        subscriptionData.period
                                    );

                                    console.log(
                                        `🧮 Calculated endDate for update ${userId}:`,
                                        {
                                            startDate:
                                                subscriptionData.startDate,
                                            period: subscriptionData.period,
                                            originalEndDate:
                                                subscriptionData.endDate,
                                            calculatedEndDate: updateEndDate,
                                            shouldCalculate:
                                                shouldCalculateUpdateEndDate,
                                        }
                                    );
                                }

                                console.log(
                                    `🔄 Updating subscriber ${userId} with newer data:`,
                                    {
                                        oldEndDate: subscriber.endDate,
                                        newEndDate: updateEndDate,
                                        oldStartDate: subscriber.startDate,
                                        newStartDate:
                                            subscriptionData.startDate,
                                    }
                                );

                                subscriber.planName = subscriptionData.planName;
                                subscriber.price = subscriptionData.price;
                                subscriber.period = subscriptionData.period;
                                subscriber.status = subscriptionData.status;
                                subscriber.paymentMethod =
                                    subscriptionData.paymentMethod;
                                subscriber.startDate =
                                    subscriptionData.startDate;
                                subscriber.endDate = updateEndDate;
                                subscriber.tripsUsed =
                                    subscriptionData.tripsUsed;
                                subscriber.tripLimit =
                                    subscriptionData.tripLimit;
                                subscriber.usedWalletMoney =
                                    subscriptionData.usedWalletMoney;
                                subscriber.payableAmount =
                                    subscriptionData.payableAmount;
                                subscriber.autoRenew =
                                    subscriptionData.autoRenew;
                                subscriber.canRenew = subscriptionData.canRenew;
                                subscriber.adminVerified =
                                    subscriptionData.adminVerified;
                                subscriber.planDeleted =
                                    subscriptionData.planDeleted;

                                console.log(
                                    `✅ Updated subscriber ${userId} endDate:`,
                                    {
                                        userId: userId,
                                        endDate: subscriber.endDate,
                                        updateEndDate: updateEndDate,
                                        endDateType: typeof subscriber.endDate,
                                        endDateIsNull:
                                            subscriber.endDate === null,
                                        endDateIsUndefined:
                                            subscriber.endDate === undefined,
                                        endDateTruthy: !!subscriber.endDate,
                                        originalEndDate:
                                            subscriptionData.endDate,
                                        startDate: subscriptionData.startDate,
                                        period: subscriptionData.period,
                                    }
                                );

                                // Critical validation for update
                                if (!subscriber.endDate) {
                                    console.error(
                                        "❌ CRITICAL ERROR: endDate is null/undefined after update!",
                                        {
                                            originalEndDate:
                                                subscriptionData.endDate,
                                            updateEndDate: updateEndDate,
                                            startDate:
                                                subscriptionData.startDate,
                                            period: subscriptionData.period,
                                            updateEndDateType:
                                                typeof updateEndDate,
                                        }
                                    );
                                }
                            }

                            // Check if subscription is expired
                            if (subscriber.endDate) {
                                const endDate = new Date(subscriber.endDate);
                                const now = new Date();
                                if (
                                    endDate < now &&
                                    subscriber.status === "active"
                                ) {
                                    console.log(
                                        `⏰ Marking subscription as expired for user ${subscriber.id}`
                                    );
                                    subscriber.status = "expired";
                                }
                            } else {
                                // Handle subscriptions without endDate
                                console.log(
                                    `⚠️ Subscription for user ${subscriber.id} has no endDate`
                                );
                                if (subscriber.status === "active") {
                                    console.log(
                                        `ℹ️ Subscription for user ${subscriber.id} is active but has no endDate`
                                    );
                                }
                            }
                        }
                    );

                    const subscribersArray = Array.from(
                        subscribersMap.values()
                    );
                    console.log(
                        "📊 Subscribers array from Firebase Admin:",
                        subscribersArray
                    );

                    // Don't return early - we need to fetch transaction history too
                    console.log(
                        "🔄 Now loading transaction history for complete data..."
                    );

                    // Get transaction history to complement subscription data
                    try {
                        const transactionsResponse =
                            await SubscriptionAPI.getSubscriptionTransactions({
                                limit: 1000,
                                offset: 0,
                            });

                        if (
                            transactionsResponse.success &&
                            transactionsResponse.transactions
                        ) {
                            console.log(
                                "📋 Loading additional transactions:",
                                transactionsResponse.transactions.length
                            );

                            // Add transaction history to existing subscribers
                            transactionsResponse.transactions.forEach(
                                (transaction) => {
                                    const userId = transaction.userId;

                                    if (subscribersMap.has(userId)) {
                                        // Format transaction data using mobile app format
                                        const formattedTransaction = {
                                            timestamp:
                                                transaction.timestamp ||
                                                transaction.start_date,
                                            type:
                                                transaction.type ||
                                                "subscription_payment",
                                            note:
                                                transaction.adminNote ||
                                                transaction.note ||
                                                "",
                                            status:
                                                transaction.status ||
                                                transaction.subscription_status,
                                            amount:
                                                transaction.amount ||
                                                transaction.price,
                                            planName:
                                                transaction.planName ||
                                                transaction.plan_name,
                                            paymentMethod:
                                                transaction.paymentMethod ||
                                                transaction.payment_method,
                                        };

                                        // Check if this transaction is already added (avoid duplicates)
                                        const existing = subscribersMap
                                            .get(userId)
                                            .transactions.find(
                                                (t) =>
                                                    t.timestamp ===
                                                        formattedTransaction.timestamp &&
                                                    t.type ===
                                                        formattedTransaction.type
                                            );

                                        if (!existing) {
                                            subscribersMap
                                                .get(userId)
                                                .transactions.push(
                                                    formattedTransaction
                                                );
                                        }
                                    }
                                }
                            );

                            // Update subscribers array with transaction data
                            const updatedArray = Array.from(
                                subscribersMap.values()
                            );
                            const finalSubscribers = await fetchMissingUserData(
                                updatedArray
                            );
                            setSubscribers(finalSubscribers);
                            return;
                        }
                    } catch (transactionError) {
                        console.error(
                            "Error loading transaction history:",
                            transactionError
                        );
                    }

                    // If transaction loading failed, use what we have
                    const updatedSubscribers = await fetchMissingUserData(
                        subscribersArray
                    );
                    setSubscribers(updatedSubscribers);
                    return;
                } else {
                    console.warn(
                        "No subscriptions found in Firebase Admin response, falling back to transactions API"
                    );
                }
            } catch (firebaseError) {
                console.error(
                    "Error loading from Firebase Admin:",
                    firebaseError
                );
                console.log(
                    "Falling back to transactions API due to Firebase Admin error"
                );
            }

            // Fallback: Get subscription transactions which have user details
            console.log(
                "🔄 Loading subscribers from transactions API as fallback..."
            );
            const transactionsResponse =
                await SubscriptionAPI.getSubscriptionTransactions({
                    limit: 1000, // Get all for now
                    offset: 0,
                });

            if (transactionsResponse.success) {
                // Group transactions by userId to get unique subscribers
                const subscribersMap = new Map();

                console.log(
                    "📡 Raw transactions from API:",
                    transactionsResponse.transactions
                );

                // Debug: Log first few transactions to see the structure
                if (transactionsResponse.transactions.length > 0) {
                    console.log(
                        "🔍 First transaction structure:",
                        transactionsResponse.transactions[0]
                    );
                    console.log(
                        "🔍 Available fields in transaction:",
                        Object.keys(transactionsResponse.transactions[0])
                    );
                }

                transactionsResponse.transactions.forEach((transaction) => {
                    console.log("📋 Processing transaction:", {
                        userId: transaction.userId,
                        planName: transaction.planName || transaction.plan_name,
                        planPeriod:
                            transaction.planPeriod || transaction.period,
                        endDate: transaction.endDate || transaction.end_date,
                        status:
                            transaction.status ||
                            transaction.subscription_status,
                        amount: transaction.amount || transaction.price,
                        user: transaction.user,
                        fullTransaction: transaction,
                    });
                    const userId = transaction.userId;

                    if (!subscribersMap.has(userId)) {
                        // Calculate endDate from period if not available
                        let transactionEndDate =
                            transaction.endDate || transaction.end_date;

                        // Get period from transaction or use default
                        let transactionPeriod =
                            transaction.planPeriod || transaction.period;
                        if (!transactionPeriod) {
                            // Try to get period from planName or use default
                            if (
                                transaction.planName &&
                                transaction.planName.includes("monthly")
                            ) {
                                transactionPeriod = "monthly";
                            } else if (
                                transaction.planName &&
                                transaction.planName.includes("yearly")
                            ) {
                                transactionPeriod = "yearly";
                            } else if (
                                transaction.planName &&
                                transaction.planName.includes("weekly")
                            ) {
                                transactionPeriod = "weekly";
                            } else if (
                                transaction.planName &&
                                transaction.planName.includes("daily")
                            ) {
                                transactionPeriod = "daily";
                            } else {
                                transactionPeriod = "monthly"; // Default fallback
                            }
                        }

                        // Always calculate endDate if it's missing, null, undefined, 0, or invalid
                        const shouldCalculateTransactionEndDate =
                            !transactionEndDate ||
                            transactionEndDate === 0 ||
                            transactionEndDate === null ||
                            transactionEndDate === undefined ||
                            (typeof transactionEndDate === "number" &&
                                transactionEndDate <= 0);

                        if (
                            shouldCalculateTransactionEndDate &&
                            (transaction.timestamp || transaction.start_date) &&
                            transactionPeriod
                        ) {
                            transactionEndDate = calculateEndDateFromPeriod(
                                transaction.timestamp || transaction.start_date,
                                transactionPeriod
                            );

                            console.log(
                                `🧮 Calculated endDate for transaction ${userId}:`,
                                {
                                    startDate:
                                        transaction.timestamp ||
                                        transaction.start_date,
                                    period: transactionPeriod,
                                    originalEndDate:
                                        transaction.endDate ||
                                        transaction.end_date,
                                    calculatedEndDate: transactionEndDate,
                                    shouldCalculate:
                                        shouldCalculateTransactionEndDate,
                                    planName: transaction.planName,
                                    inferredPeriod: transactionPeriod,
                                }
                            );
                        }

                        // Combine firstName and lastName if available
                        let fullName = "";
                        if (
                            transaction.user?.firstName ||
                            transaction.user?.lastName
                        ) {
                            if (
                                transaction.user.firstName &&
                                transaction.user.lastName
                            ) {
                                fullName = `${transaction.user.firstName} ${transaction.user.lastName}`;
                            } else if (transaction.user.firstName) {
                                fullName = transaction.user.firstName;
                            } else if (transaction.user.lastName) {
                                fullName = transaction.user.lastName;
                            }
                        } else if (
                            transaction.user?.name &&
                            transaction.user.name !== "Unknown User"
                        ) {
                            fullName = transaction.user.name;
                        } else if (
                            transaction.userName &&
                            transaction.userName !== "Unknown User"
                        ) {
                            fullName = transaction.userName;
                        }

                        const newSubscriber = {
                            id: userId,
                            name: fullName || t("unknown_user"),
                            email:
                                (transaction.user?.email &&
                                    transaction.user.email !== "-") ||
                                (transaction.userEmail &&
                                    transaction.userEmail !== "-") ||
                                "",
                            phone:
                                transaction.user?.phone ||
                                transaction.userPhone ||
                                "",
                            userType:
                                transaction.user?.type ||
                                transaction.userType ||
                                "customer",
                            planName:
                                transaction.planName || transaction.plan_name,
                            price: transaction.amount || transaction.price,
                            period: transactionPeriod, // Use the inferred period
                            status:
                                transaction.status ||
                                transaction.subscription_status,
                            paymentMethod:
                                transaction.paymentMethod ||
                                transaction.payment_method,
                            startDate:
                                transaction.timestamp || transaction.start_date,
                            endDate: transactionEndDate,
                            transactions: [],
                        };

                        console.log(
                            "👤 Creating new subscriber from transaction:",
                            {
                                userId: userId,
                                endDate: newSubscriber.endDate,
                                transactionEndDate: transactionEndDate,
                                endDateType: typeof newSubscriber.endDate,
                                endDateIsNull: newSubscriber.endDate === null,
                                endDateIsUndefined:
                                    newSubscriber.endDate === undefined,
                                endDateTruthy: !!newSubscriber.endDate,
                                originalTransactionEndDate:
                                    transaction.endDate || transaction.end_date,
                                startDate:
                                    transaction.timestamp ||
                                    transaction.start_date,
                                period: transactionPeriod, // Use the inferred period
                                fullSubscriber: newSubscriber,
                            }
                        );

                        // Critical validation for transaction
                        if (!newSubscriber.endDate) {
                            console.error(
                                "❌ CRITICAL ERROR: endDate is null/undefined in transaction subscriber!",
                                {
                                    originalEndDate:
                                        transaction.endDate ||
                                        transaction.end_date,
                                    transactionEndDate: transactionEndDate,
                                    startDate:
                                        transaction.timestamp ||
                                        transaction.start_date,
                                    period: transactionPeriod, // Use the inferred period
                                    transactionEndDateType:
                                        typeof transactionEndDate,
                                }
                            );
                        }
                        subscribersMap.set(userId, newSubscriber);
                    }

                    // Format transaction data for history display using mobile app format
                    const formattedTransaction = {
                        timestamp:
                            transaction.timestamp || transaction.start_date,
                        type: transaction.type || "subscription_payment",
                        note: transaction.adminNote || transaction.note || "",
                        status:
                            transaction.status ||
                            transaction.subscription_status,
                        amount: transaction.amount || transaction.price,
                        planName: transaction.planName || transaction.plan_name,
                        paymentMethod:
                            transaction.paymentMethod ||
                            transaction.payment_method,
                    };

                    subscribersMap
                        .get(userId)
                        .transactions.push(formattedTransaction);

                    // Update with most recent transaction data
                    const subscriber = subscribersMap.get(userId);
                    if (
                        new Date(transaction.timestamp) >
                        new Date(subscriber.startDate)
                    ) {
                        // Calculate endDate from period if not available for update
                        let updateTransactionEndDate =
                            transaction.endDate || transaction.end_date;

                        // Get period from transaction or use default (same logic as above)
                        let updateTransactionPeriod =
                            transaction.planPeriod || transaction.period;
                        if (!updateTransactionPeriod) {
                            // Try to get period from planName or use default
                            if (
                                transaction.planName &&
                                transaction.planName.includes("monthly")
                            ) {
                                updateTransactionPeriod = "monthly";
                            } else if (
                                transaction.planName &&
                                transaction.planName.includes("yearly")
                            ) {
                                updateTransactionPeriod = "yearly";
                            } else if (
                                transaction.planName &&
                                transaction.planName.includes("weekly")
                            ) {
                                updateTransactionPeriod = "weekly";
                            } else if (
                                transaction.planName &&
                                transaction.planName.includes("daily")
                            ) {
                                updateTransactionPeriod = "daily";
                            } else {
                                updateTransactionPeriod = "monthly"; // Default fallback
                            }
                        }

                        // Always calculate endDate if it's missing, null, undefined, 0, or invalid
                        const shouldCalculateUpdateTransactionEndDate =
                            !updateTransactionEndDate ||
                            updateTransactionEndDate === 0 ||
                            updateTransactionEndDate === null ||
                            updateTransactionEndDate === undefined ||
                            (typeof updateTransactionEndDate === "number" &&
                                updateTransactionEndDate <= 0);

                        if (
                            shouldCalculateUpdateTransactionEndDate &&
                            (transaction.timestamp || transaction.start_date) &&
                            updateTransactionPeriod
                        ) {
                            updateTransactionEndDate =
                                calculateEndDateFromPeriod(
                                    transaction.timestamp ||
                                        transaction.start_date,
                                    updateTransactionPeriod
                                );

                            console.log(
                                `🧮 Calculated endDate for transaction update ${userId}:`,
                                {
                                    startDate:
                                        transaction.timestamp ||
                                        transaction.start_date,
                                    period: updateTransactionPeriod,
                                    originalEndDate:
                                        transaction.endDate ||
                                        transaction.end_date,
                                    calculatedEndDate: updateTransactionEndDate,
                                    shouldCalculate:
                                        shouldCalculateUpdateTransactionEndDate,
                                    planName: transaction.planName,
                                    inferredPeriod: updateTransactionPeriod,
                                }
                            );
                        }

                        console.log(
                            "🔄 Updating subscriber with newer transaction:",
                            {
                                name: subscriber.name,
                                email: subscriber.email,
                                oldPeriod: subscriber.period,
                                newPeriod: updateTransactionPeriod, // Use the inferred period
                                oldEndDate: subscriber.endDate,
                                newEndDate: updateTransactionEndDate,
                            }
                        );

                        subscriber.planName =
                            transaction.planName || transaction.plan_name;
                        subscriber.price =
                            transaction.amount || transaction.price;
                        subscriber.period = updateTransactionPeriod; // Use the inferred period

                        // Check for expiration before setting status
                        let statusToSet =
                            transaction.status ||
                            transaction.subscription_status;
                        const endDate = new Date(transaction.endDate);
                        const now = new Date();
                        if (endDate < now && transaction.status === "active") {
                            statusToSet = "expired";
                        }
                        subscriber.status = statusToSet;

                        subscriber.paymentMethod =
                            transaction.paymentMethod ||
                            transaction.payment_method;
                        subscriber.startDate =
                            transaction.timestamp || transaction.start_date;
                        subscriber.endDate = updateTransactionEndDate;

                        console.log(
                            `✅ Updated transaction subscriber ${userId} endDate:`,
                            {
                                userId: userId,
                                endDate: subscriber.endDate,
                                updateTransactionEndDate:
                                    updateTransactionEndDate,
                                endDateType: typeof subscriber.endDate,
                                endDateIsNull: subscriber.endDate === null,
                                endDateIsUndefined:
                                    subscriber.endDate === undefined,
                                endDateTruthy: !!subscriber.endDate,
                                originalTransactionEndDate:
                                    transaction.endDate || transaction.end_date,
                                startDate:
                                    transaction.timestamp ||
                                    transaction.start_date,
                                period: updateTransactionPeriod, // Use the inferred period
                            }
                        );

                        // Critical validation for transaction update
                        if (!subscriber.endDate) {
                            console.error(
                                "❌ CRITICAL ERROR: endDate is null/undefined after transaction update!",
                                {
                                    originalEndDate:
                                        transaction.endDate ||
                                        transaction.end_date,
                                    updateTransactionEndDate:
                                        updateTransactionEndDate,
                                    startDate:
                                        transaction.timestamp ||
                                        transaction.start_date,
                                    period: updateTransactionPeriod, // Use the inferred period
                                    updateTransactionEndDateType:
                                        typeof updateTransactionEndDate,
                                }
                            );
                        }

                        // Also update user data if available
                        if (
                            transaction.user?.name &&
                            transaction.user.name !== "Unknown User"
                        )
                            subscriber.name = transaction.user.name;
                        if (
                            transaction.user?.email &&
                            transaction.user.email !== "-"
                        )
                            subscriber.email = transaction.user.email;
                        if (
                            transaction.user?.phone &&
                            transaction.user.phone !== "-"
                        )
                            subscriber.phone = transaction.user.phone;
                        if (transaction.user?.type)
                            subscriber.userType = transaction.user.type;

                        // Also check for direct user fields
                        if (
                            transaction.userName &&
                            transaction.userName !== "Unknown User"
                        )
                            subscriber.name = transaction.userName;
                        if (
                            transaction.userEmail &&
                            transaction.userEmail !== "-"
                        )
                            subscriber.email = transaction.userEmail;
                        if (
                            transaction.userPhone &&
                            transaction.userPhone !== "-"
                        )
                            subscriber.phone = transaction.userPhone;
                        if (transaction.userType)
                            subscriber.userType = transaction.userType;

                        // Try to combine firstName and lastName if available
                        if (
                            transaction.user?.firstName ||
                            transaction.user?.lastName
                        ) {
                            let fullName = "";
                            if (
                                transaction.user.firstName &&
                                transaction.user.lastName
                            ) {
                                fullName = `${transaction.user.firstName} ${transaction.user.lastName}`;
                            } else if (transaction.user.firstName) {
                                fullName = transaction.user.firstName;
                            } else if (transaction.user.lastName) {
                                fullName = transaction.user.lastName;
                            }
                            if (fullName && fullName !== "Unknown User") {
                                subscriber.name = fullName;
                            }
                        }
                    }

                    // Check if subscription is expired based on endDate
                    if (subscriber.endDate) {
                        const endDate = new Date(subscriber.endDate);
                        const now = new Date();
                        if (endDate < now && subscriber.status === "active") {
                            console.log(
                                `⏰ Marking subscription as expired for user ${subscriber.name} (${subscriber.email})`
                            );
                            console.log(
                                `📅 End date: ${endDate}, Current date: ${now}`
                            );
                            console.log(
                                `📊 Days overdue: ${Math.floor(
                                    (now - endDate) / (1000 * 60 * 60 * 24)
                                )}`
                            );
                            subscriber.status = "expired";
                        }
                    }

                    // Also check for any other status that might need to be expired
                    if (
                        subscriber.endDate &&
                        subscriber.status !== "cancelled" &&
                        subscriber.status !== "expired"
                    ) {
                        const endDate = new Date(subscriber.endDate);
                        const now = new Date();
                        if (endDate < now) {
                            console.log(
                                `⏰ Marking subscription as expired for user ${subscriber.name} (${subscriber.email}) - status was: ${subscriber.status}`
                            );
                            subscriber.status = "expired";
                        }
                    }
                });

                const subscribersArray = Array.from(subscribersMap.values());
                console.log("Loaded subscribers:", subscribersArray);

                // Log unique plan names for debugging
                const uniquePlanNames = [
                    ...new Set(
                        subscribersArray
                            .map((sub) => sub.planName)
                            .filter(Boolean)
                    ),
                ];
                console.log("Available plan names:", uniquePlanNames);
                console.log(
                    "Plan names with counts:",
                    uniquePlanNames.map((planName) => ({
                        planName,
                        count: subscribersArray.filter(
                            (sub) => sub.planName === planName
                        ).length,
                    }))
                );

                // Log unique statuses for debugging
                const uniqueStatuses = [
                    ...new Set(
                        subscribersArray
                            .map((sub) => sub.status)
                            .filter(Boolean)
                    ),
                ];
                console.log("Available statuses:", uniqueStatuses);
                console.log(
                    "Statuses with counts:",
                    uniqueStatuses.map((status) => ({
                        status,
                        count: subscribersArray.filter(
                            (sub) => sub.status === status
                        ).length,
                    }))
                );

                // Log unique periods for debugging
                const uniquePeriods = [
                    ...new Set(
                        subscribersArray
                            .map((sub) => sub.period)
                            .filter(Boolean)
                    ),
                ];
                console.log("Available periods:", uniquePeriods);
                console.log(
                    "Periods with counts:",
                    uniquePeriods.map((period) => ({
                        period,
                        count: subscribersArray.filter(
                            (sub) => sub.period === period
                        ).length,
                    }))
                );

                // Log subscribers with missing data
                const subscribersWithMissingData = subscribersArray.filter(
                    (sub) => !sub.period || !sub.endDate || !sub.planName
                );
                console.log(
                    `⚠️ Subscribers with missing data (${subscribersWithMissingData.length}):`,
                    subscribersWithMissingData.map((sub) => ({
                        name: sub.name,
                        email: sub.email,
                        period: sub.period,
                        endDate: sub.endDate,
                        planName: sub.planName,
                        status: sub.status,
                    }))
                );

                // Log expired subscriptions specifically
                const expiredSubscriptions = subscribersArray.filter(
                    (sub) => sub.status === "expired"
                );
                console.log(
                    `📊 Found ${expiredSubscriptions.length} expired subscriptions:`,
                    expiredSubscriptions.map((sub) => ({
                        name: sub.name,
                        email: sub.email,
                        endDate: sub.endDate,
                        status: sub.status,
                    }))
                );

                // Log all subscribers with endDate for debugging
                const subscribersWithEndDate = subscribersArray.filter(
                    (sub) => sub.endDate
                );
                console.log(
                    `📅 Subscribers with endDate (${subscribersWithEndDate.length}):`,
                    subscribersWithEndDate.map((sub) => ({
                        name: sub.name,
                        email: sub.email,
                        endDate: sub.endDate,
                        status: sub.status,
                        isExpired: new Date(sub.endDate) < new Date(),
                    }))
                );

                // Log subscribers that should be expired but aren't
                const shouldBeExpired = subscribersArray.filter((sub) => {
                    if (!sub.endDate || sub.status !== "active") return false;
                    const endDate = new Date(sub.endDate);
                    const now = new Date();
                    return endDate < now;
                });
                console.log(
                    `⚠️ Subscribers that should be expired but aren't (${shouldBeExpired.length}):`,
                    shouldBeExpired.map((sub) => ({
                        name: sub.name,
                        email: sub.email,
                        endDate: sub.endDate,
                        status: sub.status,
                        daysOverdue: Math.floor(
                            (new Date() - new Date(sub.endDate)) /
                                (1000 * 60 * 60 * 24)
                        ),
                    }))
                );

                // Log all subscribers with their status and endDate for debugging
                console.log(
                    `📋 All subscribers status summary:`,
                    subscribersArray.map((sub) => ({
                        name: sub.name,
                        email: sub.email,
                        status: sub.status,
                        endDate: sub.endDate,
                        period: sub.period,
                        planName: sub.planName,
                        hasEndDate: !!sub.endDate,
                        isExpired: sub.endDate
                            ? new Date(sub.endDate) < new Date()
                            : false,
                        rawEndDate: sub.endDate,
                        rawPeriod: sub.period,
                    }))
                );

                // Log subscribers with missing endDate
                const subscribersWithoutEndDate = subscribersArray.filter(
                    (sub) => !sub.endDate
                );
                console.log(
                    `⚠️ Subscribers without endDate (${subscribersWithoutEndDate.length}):`,
                    subscribersWithoutEndDate.map((sub) => ({
                        name: sub.name,
                        email: sub.email,
                        planName: sub.planName,
                        status: sub.status,
                        rawData: sub, // Log the entire subscriber object to see what's missing
                    }))
                );

                // Additional debugging: Check if endDate exists but is null/undefined/empty
                console.log("🔍 Detailed endDate analysis:");
                subscribersArray.forEach((sub, index) => {
                    console.log(`Subscriber ${index + 1}:`, {
                        name: sub.name,
                        email: sub.email,
                        endDate: sub.endDate,
                        endDateType: typeof sub.endDate,
                        endDateValue: sub.endDate,
                        hasEndDateProperty: "endDate" in sub,
                        endDateIsNull: sub.endDate === null,
                        endDateIsUndefined: sub.endDate === undefined,
                        endDateIsEmpty: sub.endDate === "",
                        startDate: sub.startDate,
                        period: sub.period,
                        status: sub.status,
                    });
                });

                // Fetch missing user data for subscribers with incomplete information
                console.log(
                    `🔄 About to call fetchMissingUserData with ${subscribersArray.length} subscribers`
                );
                console.log(
                    `📋 Subscribers before fetchMissingUserData:`,
                    subscribersArray.map((sub) => ({
                        id: sub.id,
                        name: sub.name,
                        email: sub.email,
                    }))
                );
                const updatedSubscribers = await fetchMissingUserData(
                    subscribersArray
                );
                console.log(
                    `✅ fetchMissingUserData completed, setting ${updatedSubscribers.length} subscribers`
                );
                console.log(
                    `📋 Subscribers after fetchMissingUserData:`,
                    updatedSubscribers.map((sub) => ({
                        id: sub.id,
                        name: sub.name,
                        email: sub.email,
                    }))
                );
                setSubscribers(updatedSubscribers);
            } else {
                setError(
                    transactionsResponse.error ||
                        t("failed_to_load_subscribers")
                );
            }
        } catch (err) {
            console.error("Error loading subscribers:", err);
            setError(t("error_loading_data"));
        } finally {
            setLoading(false);
        }
    }, [t]);

    // Helper function to fetch missing user data
    const fetchMissingUserData = async (subscribers) => {
        console.log(
            `🔍 fetchMissingUserData called with ${subscribers.length} subscribers`
        );
        const updatedSubscribers = [...subscribers];

        for (let i = 0; i < updatedSubscribers.length; i++) {
            const subscriber = updatedSubscribers[i];

            const isUnknownUser =
                !subscriber.name ||
                subscriber.name === "Unknown User" ||
                subscriber.name === t("unknown_user") ||
                subscriber.name === "Usuario desconocido";
            const isEmailEmpty =
                !subscriber.email ||
                subscriber.email === "" ||
                subscriber.email === "-";

            console.log(`🔍 Detailed check for ${subscriber.id}:`, {
                name: subscriber.name,
                email: subscriber.email,
                nameIsEmpty: !subscriber.name,
                nameIsUnknownUser: subscriber.name === "Unknown User",
                nameIsTranslatedUnknown: subscriber.name === t("unknown_user"),
                nameIsSpanishUnknown: subscriber.name === "Usuario desconocido",
                emailIsEmpty: !subscriber.email,
                emailIsDash: subscriber.email === "-",
                emailIsEmptyString: subscriber.email === "",
                isUnknownUser,
                isEmailEmpty,
            });

            console.log(`🔍 Checking subscriber ${i}:`, {
                id: subscriber.id,
                name: subscriber.name,
                email: subscriber.email,
                isUnknownUser: isUnknownUser,
                isEmailEmpty: isEmailEmpty,
                willFetch: isUnknownUser || isEmailEmpty,
            });

            // Check if user data is missing or incomplete
            if (isUnknownUser || isEmailEmpty) {
                console.log(
                    `🔍 Will fetch user data for ${subscriber.id} because:`,
                    { isUnknownUser, isEmailEmpty }
                );
                // Skip if userId contains invalid characters for Firebase Database
                if (
                    subscriber.id &&
                    (subscriber.id.includes(".") ||
                        subscriber.id.includes("#") ||
                        subscriber.id.includes("$") ||
                        subscriber.id.includes("[") ||
                        subscriber.id.includes("]"))
                ) {
                    console.log(
                        `⚠️ Skipping user data fetch for ${subscriber.id} - contains invalid characters`
                    );
                    continue;
                }
                try {
                    console.log(
                        `🔍 Fetching user data for ${subscriber.id} from users collection...`
                    );
                    const userResponse = await fetch(
                        `${SubscriptionAPI.getBaseUrl()}/getuser?userId=${
                            subscriber.id
                        }`,
                        {
                            method: "GET",
                            headers: {
                                "Content-Type": "application/json",
                            },
                        }
                    );

                    if (userResponse.ok) {
                        const userData = await userResponse.json();
                        console.log(
                            `📡 Raw response for ${subscriber.id}:`,
                            userData
                        );
                        if (userData.success && userData.user) {
                            console.log(
                                `✅ Found user data for ${subscriber.id}:`,
                                userData.user
                            );
                            // Combine firstName and lastName if available
                            let fullName = userData.user.name;
                            if (
                                !fullName &&
                                (userData.user.firstName ||
                                    userData.user.lastName)
                            ) {
                                if (
                                    userData.user.firstName &&
                                    userData.user.lastName
                                ) {
                                    fullName = `${userData.user.firstName} ${userData.user.lastName}`;
                                } else if (userData.user.firstName) {
                                    fullName = userData.user.firstName;
                                } else if (userData.user.lastName) {
                                    fullName = userData.user.lastName;
                                }
                            }

                            updatedSubscribers[i] = {
                                ...subscriber,
                                name:
                                    fullName ||
                                    subscriber.name ||
                                    t("unknown_user"),
                                email:
                                    userData.user.email ||
                                    subscriber.email ||
                                    "",
                                phone:
                                    userData.user.phone ||
                                    subscriber.phone ||
                                    "",
                                userType:
                                    userData.user.type ||
                                    subscriber.userType ||
                                    "customer",
                            };
                        } else {
                            console.warn(
                                `⚠️ No user data found for ${subscriber.id}`
                            );
                        }
                    } else {
                        console.warn(
                            `⚠️ Failed to fetch user data for ${subscriber.id}:`,
                            userResponse.status
                        );
                    }
                } catch (error) {
                    console.error(
                        `❌ Error fetching user data for ${subscriber.id}:`,
                        error
                    );
                }
            }
        }

        return updatedSubscribers;
    };

    const applyFilters = useCallback(() => {
        let filtered = [...subscribers];

        if (filterPlan) {
            console.log(`🔍 Filtering by plan: "${filterPlan}"`);
            console.log(
                `📊 Total subscribers before plan filter: ${filtered.length}`
            );

            filtered = filtered.filter((sub) => {
                // Extract plan name from filter (remove price and period info)
                const filterPlanName = filterPlan.split(" - ")[0];
                const matches = sub.planName === filterPlanName;
                console.log(
                    `📋 Subscriber ${sub.id}: planName="${sub.planName}", filterPlanName="${filterPlanName}", matches=${matches}`
                );
                return matches;
            });

            console.log(
                `📊 Total subscribers after plan filter: ${filtered.length}`
            );
        }

        if (filterStatus) {
            console.log(`🔍 Filtering by status: ${filterStatus}`);
            console.log(
                `📊 Subscribers before status filter: ${filtered.length}`
            );
            filtered = filtered.filter((sub) => {
                // Handle status mapping for consistency
                if (filterStatus === "active") {
                    // Include both 'active' and 'success' status when filtering for active
                    return sub.status === "active" || sub.status === "success";
                }
                return sub.status === filterStatus;
            });
            console.log(
                `📊 Subscribers after status filter: ${filtered.length}`
            );
            console.log(
                `📋 Filtered subscribers:`,
                filtered.map((sub) => ({ name: sub.name, status: sub.status }))
            );
        }

        if (filterType) {
            filtered = filtered.filter((sub) => sub.userType === filterType);
        }

        setFilteredSubscribers(filtered);
    }, [subscribers, filterPlan, filterStatus, filterType]);

    useEffect(() => {
        loadSubscribers();
        loadAllPlans(); // Load plans for filter dropdown
    }, [currentLanguage, loadSubscribers]);

    useEffect(() => {
        applyFilters();
    }, [subscribers, filterPlan, filterStatus, filterType, applyFilters]);

    const handleEditSubscriber = (subscriber) => {
        setSelectedSubscriber(subscriber);
        setEditFormData({
            status: subscriber.status,
            adminNote: subscriber.adminNote || "",
        });
        setEditModalOpen(true);
    };

    const handleSaveEdit = async () => {
        try {
            if (!selectedSubscriber) {
                showSnackbar(t("no_subscriber_selected"), "error");
                return;
            }

            console.log("🔄 Updating subscription status:", {
                selectedSubscriber: selectedSubscriber,
                userId: selectedSubscriber.id, // Use 'id' not 'userId'
                newStatus: editFormData.status,
                adminNote: editFormData.adminNote,
            });

            // Update subscription status using API
            const result = await SubscriptionAPI.updateSubscriptionStatus(
                selectedSubscriber.id, // Use 'id' not 'userId'
                editFormData.status,
                editFormData.adminNote
            );

            if (result.success) {
                showSnackbar(t("subscriber_updated_successfully"), "success");
                setEditModalOpen(false);
                loadSubscribers(); // Refresh the list
            } else {
                console.error("❌ Update failed:", result.error);
                showSnackbar(
                    result.error || t("error_updating_subscriber"),
                    "error"
                );
            }
        } catch (error) {
            console.error("❌ Error updating subscriber:", error);
            showSnackbar(
                error.message || t("error_updating_subscriber"),
                "error"
            );
        }
    };

    const handleApprovePayment = async (subscriber) => {
        try {
            console.log("🔄 Approving payment for subscriber:", subscriber);

            const result = await SubscriptionAPI.updateSubscriptionStatus(
                subscriber.id,
                "active",
                "Payment approved by admin"
            );

            if (result.success) {
                showSnackbar(t("payment_approved_successfully"), "success");
                loadSubscribers();
            } else {
                console.error("❌ Approve failed:", result.error);
                showSnackbar(
                    result.error || t("error_approving_payment"),
                    "error"
                );
            }
        } catch (error) {
            console.error("❌ Error approving payment:", error);
            showSnackbar(
                error.message || t("error_approving_payment"),
                "error"
            );
        }
    };

    const handleRejectPayment = async (subscriber) => {
        try {
            console.log("🔄 Rejecting payment for subscriber:", subscriber);

            const result = await SubscriptionAPI.updateSubscriptionStatus(
                subscriber.id,
                "cancelled",
                "Payment rejected by admin"
            );

            if (result.success) {
                showSnackbar(t("payment_rejected_successfully"), "success");
                loadSubscribers();
            } else {
                console.error("❌ Reject failed:", result.error);
                showSnackbar(
                    result.error || t("error_rejecting_payment"),
                    "error"
                );
            }
        } catch (error) {
            console.error("❌ Error rejecting payment:", error);
            showSnackbar(
                error.message || t("error_rejecting_payment"),
                "error"
            );
        }
    };

    const handleExport = () => {
        try {
            // Prepare CSV data
            const csvData = filteredSubscribers.map((subscriber) => ({
                Name: subscriber.name || t("unknown_user"),
                Email: subscriber.email || "-",
                Phone: subscriber.phone || "-",
                "User Type": t(subscriber.userType) || subscriber.userType,
                "Plan Name": subscriber.planName || "-",
                Price: formatPrice(subscriber.price) || "-",
                Period: t(subscriber.period) || subscriber.period,
                Status: formatStatus(subscriber.status, t),
                "Payment Method":
                    t(subscriber.paymentMethod) || subscriber.paymentMethod,
                "Start Date": formatDate(subscriber.startDate),
                "End Date": (() => {
                    // Calculate endDate if not available
                    let displayEndDate = subscriber.endDate;

                    if (
                        !displayEndDate &&
                        subscriber.startDate &&
                        subscriber.period
                    ) {
                        displayEndDate = calculateEndDateFromPeriod(
                            subscriber.startDate,
                            subscriber.period
                        );
                    }

                    return formatDate(displayEndDate);
                })(),
                "Total Transactions": subscriber.transactions?.length || 0,
            }));

            // Convert to CSV format
            const headers = Object.keys(csvData[0] || {});
            const csvContent = [
                headers.join(","),
                ...csvData.map((row) =>
                    headers
                        .map((header) => {
                            const value = row[header] || "";
                            // Escape commas and quotes in CSV
                            if (
                                typeof value === "string" &&
                                (value.includes(",") ||
                                    value.includes('"') ||
                                    value.includes("\n"))
                            ) {
                                return `"${value.replace(/"/g, '""')}"`;
                            }
                            return value;
                        })
                        .join(",")
                ),
            ].join("\n");

            // Download CSV file
            const fileName = `subscription_subscribers_${
                new Date().toISOString().split("T")[0]
            }.csv`;
            downloadCsv(csvContent, fileName);

            showSnackbar(t("csv_exported_successfully"), "success");
        } catch (error) {
            console.error("Error exporting CSV:", error);
            showSnackbar(t("error_exporting_csv"), "error");
        }
    };

    // Load available plans for change plan functionality
    const loadAvailablePlans = async () => {
        try {
            const data = await SubscriptionAPI.getSubscriptionPlans(
                null,
                currentLanguage
            );
            if (data.success) {
                // Convert plans object to array format
                const plansArray = Object.entries(data.plans || {}).map(
                    ([key, plan]) => ({
                        ...plan,
                        key: key, // Add key for consistency
                    })
                );
                setAvailablePlans(plansArray);
            }
        } catch (error) {
            console.error("Error loading plans:", error);
        }
    };

    // Load all plans for filter dropdown
    const loadAllPlans = async () => {
        try {
            setPlansLoading(true);
            setPlansError("");
            const data = await SubscriptionAPI.getSubscriptionPlans(
                null,
                currentLanguage
            );
            if (data.success) {
                // Convert plans object to array format for filter dropdown
                const plansArray = Object.entries(data.plans || {}).map(
                    ([key, plan]) => ({
                        id: key,
                        name:
                            plan.translations?.[data.language]?.name ||
                            plan.name,
                        price: plan.price,
                        period: plan.period,
                        userType: plan.userType || "customer",
                    })
                );
                setAllPlans(plansArray);
                console.log("📋 Loaded plans for filter:", plansArray);
            } else {
                console.error("Failed to load plans:", data.error);
                setPlansError(data.error || "Failed to load plans");
            }
        } catch (error) {
            console.error("Error loading plans for filter:", error);
            setPlansError("Network error loading plans");
        } finally {
            setPlansLoading(false);
        }
    };

    // Handle change plan
    const handleChangePlan = async (subscriber) => {
        setSelectedSubscriber(subscriber);
        await loadAvailablePlans();
        setChangePlanModalOpen(true);
    };

    // Handle plan change confirmation
    const handleConfirmPlanChange = async () => {
        if (!selectedNewPlan) {
            showSnackbar(t("please_select_a_plan"), "error");
            return;
        }

        try {
            const result = await SubscriptionAPI.changeSubscriptionPlan(
                selectedSubscriber.id,
                selectedNewPlan.id,
                "Plan changed by admin"
            );

            if (result.success) {
                showSnackbar(t("plan_changed_successfully"), "success");
                setChangePlanModalOpen(false);
                setSelectedNewPlan(null);
                loadSubscribers();
            } else {
                showSnackbar(result.error || t("error_changing_plan"), "error");
            }
        } catch (error) {
            showSnackbar(t("error_changing_plan"), "error");
        }
    };

    // Handle cancel subscription
    const handleCancelSubscription = async (subscriber) => {
        setSelectedSubscriber(subscriber);
        setCancelModalOpen(true);
    };

    // Handle cancel confirmation
    const handleConfirmCancel = async () => {
        if (!cancelReason.trim()) {
            showSnackbar(t("please_provide_cancel_reason"), "error");
            return;
        }

        try {
            const result = await SubscriptionAPI.cancelSubscription(
                selectedSubscriber.id,
                cancelReason,
                "admin"
            );

            if (result.success) {
                showSnackbar(
                    t("subscription_cancelled_successfully"),
                    "success"
                );
                setCancelModalOpen(false);
                setCancelReason("");
                loadSubscribers();
            } else {
                showSnackbar(
                    result.error || t("error_cancelling_subscription"),
                    "error"
                );
            }
        } catch (error) {
            showSnackbar(t("error_cancelling_subscription"), "error");
        }
    };

    // Handle view history
    const handleViewHistory = async (subscriber) => {
        console.log(`🔍 Viewing history for subscriber:`, subscriber);
        console.log(
            `📋 Transactions count:`,
            subscriber.transactions?.length || 0
        );
        console.log(`📋 Transactions data:`, subscriber.transactions);
        setSelectedSubscriber(subscriber);
        setHistoryModalOpen(true);
    };

    const showSnackbar = (message, severity = "info") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    // Using utility function from subscriptionUtils.js
    // const getStatusColor = (status) => { ... } - REMOVED

    const formatDate = (timestamp) => {
        if (!timestamp) return "-";

        try {
            const date = new Date(timestamp);
            if (isNaN(date.getTime())) {
                return "-";
            }
            return date.toLocaleDateString();
        } catch (error) {
            console.error("Date formatting error:", error);
            return "-";
        }
    };

    const formatPrice = (price) => {
        return `$${parseFloat(price || 0).toFixed(2)}`;
    };

    // Helper function untuk membersihkan nama plan
    const formatPlanName = (planName) => {
        if (!planName) return t("unknown_plan");

        // Jika nama plan diawali dengan 'plan_', cari nama asli dari data subscription plans
        if (planName.startsWith("plan_")) {
            const matchingPlan = allPlans.find((plan) => plan.id === planName);
            if (matchingPlan && matchingPlan.name) {
                console.log(
                    `📋 Found matching plan: ${planName} -> ${matchingPlan.name}`
                );
                return matchingPlan.name;
            }

            // Fallback jika tidak ditemukan di allPlans: tampilkan sebagai "Plan" dengan ID pendek
            if (planName.length > 20) {
                const shortId =
                    planName.split("_")[1]?.substring(0, 8) || "unknown";
                return `${t("plan")} ${shortId}`;
            }
        }

        // Jika nama plan mengandung karakter aneh atau terlalu panjang, bersihkan
        if (planName.length > 50) {
            return planName.substring(0, 50) + "...";
        }

        // Bersihkan nama plan dari karakter khusus yang tidak perlu
        return planName.replace(/[^\w\s\-]/g, "").trim() || t("unnamed_plan");
    };

    // Helper function untuk format waktu yang tersisa (seperti di mobile app)
    const formatTimeRemaining = (endDate) => {
        if (!endDate)
            return {
                text: t("no_end_date"),
                color: "#FF9800",
                isExpired: false,
            };

        try {
            const now = new Date();
            const end = new Date(endDate);

            if (isNaN(end.getTime())) {
                console.warn(
                    "Invalid endDate in formatTimeRemaining:",
                    endDate
                );
                return {
                    text: t("invalid_date"),
                    color: "#f44336",
                    isExpired: false,
                };
            }

            const timeRemaining = end.getTime() - now.getTime();

            if (timeRemaining <= 0) {
                const daysAgo = Math.ceil(
                    Math.abs(timeRemaining) / (1000 * 60 * 60 * 24)
                );
                return {
                    text: `${t("expired")} (${daysAgo} ${
                        daysAgo === 1 ? t("day") : t("days")
                    } ${t("ago")})`,
                    color: "#f44336",
                    isExpired: true,
                };
            }

            const daysRemaining = Math.ceil(
                timeRemaining / (1000 * 60 * 60 * 24)
            );
            const hoursRemaining = Math.ceil(timeRemaining / (1000 * 60 * 60));
            const minutesRemaining = Math.ceil(timeRemaining / (1000 * 60));

            let text = "";
            let color = "#4CAF50";

            if (daysRemaining > 0) {
                text = `${daysRemaining} ${
                    daysRemaining === 1
                        ? t("day_remaining")
                        : t("days_remaining")
                }`;
                if (daysRemaining <= 7) color = "#f44336";
                else if (daysRemaining <= 14) color = "#FF9800";
            } else if (hoursRemaining > 0) {
                text = `${hoursRemaining} ${
                    hoursRemaining === 1
                        ? t("hour_remaining")
                        : t("hours_remaining")
                }`;
                color = "#f44336";
            } else if (minutesRemaining > 0) {
                text = `${minutesRemaining} ${
                    minutesRemaining === 1
                        ? t("minute_remaining")
                        : t("minutes_remaining")
                }`;
                color = "#f44336";
            } else {
                text = t("expires_soon");
                color = "#f44336";
            }

            return { text, color, isExpired: false };
        } catch (error) {
            console.error(
                "Error in formatTimeRemaining:",
                error,
                "endDate:",
                endDate
            );
            return {
                text: t("error_loading_date"),
                color: "#f44336",
                isExpired: false,
            };
        }
    };

    // Helper function to get actual current status based on endDate
    const getActualStatus = (originalStatus, subscriber) => {
        // If subscription is already cancelled, keep it as cancelled
        if (originalStatus === "cancelled") {
            return originalStatus;
        }

        // Check if subscription should be expired based on current endDate
        if (subscriber && subscriber.endDate) {
            const endDate = new Date(subscriber.endDate);
            const now = new Date();

            if (
                !isNaN(endDate.getTime()) &&
                endDate < now &&
                originalStatus === "active"
            ) {
                return "expired";
            }
        }

        return originalStatus;
    };

    // Helper function untuk format tanggal yang lebih baik
    const formatExpiryDate = (endDate) => {
        if (!endDate) return "-";

        try {
            const date = new Date(endDate);
            if (isNaN(date.getTime())) {
                console.warn("Invalid endDate:", endDate);
                return "-";
            }

            // Get locale based on current language
            const locale = currentLanguage === "es" ? "es-ES" : "en-US";

            // Always use consistent date format instead of relative terms like "tomorrow"
            return date.toLocaleDateString(locale, {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch (error) {
            console.error(
                "Error formatting endDate:",
                error,
                "endDate:",
                endDate
            );
            return "-";
        }
    };

    const renderExpandedRow = (rowData) => (
        <div className={classes.expandedContent}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography className={classes.detailTitle}>
                                {t("contact_information")}
                            </Typography>
                            <Typography variant="body2">
                                <strong>{t("email")}:</strong> {rowData.email}
                            </Typography>
                            <Typography variant="body2">
                                <strong>{t("phone")}:</strong> {rowData.phone}
                            </Typography>
                            <Typography variant="body2">
                                <strong>{t("user_type")}:</strong>{" "}
                                {t(rowData.userType)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography className={classes.detailTitle}>
                                {t("subscription_details")}
                            </Typography>
                            <Typography variant="body2">
                                <strong>{t("plan")}:</strong>{" "}
                                {rowData.planName || "-"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>{t("price")}:</strong>{" "}
                                {formatPrice(rowData.price)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>{t("period")}:</strong>{" "}
                                {rowData.period
                                    ? t(rowData.period)
                                    : t("monthly")}
                            </Typography>
                            <Typography variant="body2">
                                <strong>{t("payment_method")}:</strong>{" "}
                                {rowData.paymentMethod
                                    ? t(rowData.paymentMethod)
                                    : "-"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>{t("start_date")}:</strong>{" "}
                                {formatDate(rowData.startDate)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>{t("end_date")}:</strong>{" "}
                                {(() => {
                                    // Calculate endDate if not available
                                    let displayEndDate = rowData.endDate;
                                    let isCalculated = false;

                                    if (
                                        !displayEndDate &&
                                        rowData.startDate &&
                                        rowData.period
                                    ) {
                                        displayEndDate =
                                            calculateEndDateFromPeriod(
                                                rowData.startDate,
                                                rowData.period
                                            );
                                        isCalculated = true;
                                    }

                                    return (
                                        <>
                                            {formatExpiryDate(displayEndDate)}
                                            {displayEndDate && (
                                                <span
                                                    style={{
                                                        color: formatTimeRemaining(
                                                            displayEndDate
                                                        ).color,
                                                        fontWeight: "bold",
                                                        marginLeft: 8,
                                                    }}
                                                >
                                                    (
                                                    {
                                                        formatTimeRemaining(
                                                            displayEndDate
                                                        ).text
                                                    }
                                                    )
                                                    {isCalculated && (
                                                        <span
                                                            style={{
                                                                color: "#FF9800",
                                                                marginLeft: 4,
                                                            }}
                                                        >
                                                            ({t("calculated")})
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                            {!displayEndDate && (
                                                <span
                                                    style={{
                                                        color: "#FF9800",
                                                        fontWeight: "bold",
                                                        marginLeft: 8,
                                                    }}
                                                >
                                                    ({t("no_end_date")})
                                                </span>
                                            )}
                                        </>
                                    );
                                })()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Transaction History Table */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography className={classes.detailTitle}>
                                {t("transaction_history")}
                            </Typography>
                            <MaterialTable
                                title=""
                                columns={[
                                    {
                                        title: t("date"),
                                        field: "timestamp",
                                        render: (rowData) =>
                                            formatDate(rowData.timestamp),
                                        cellStyle: { fontSize: "0.8rem" },
                                    },
                                    {
                                        title: t("type"),
                                        field: "type",
                                        render: (rowData) => {
                                            // Handle different transaction types
                                            const typeLower =
                                                rowData.type?.toLowerCase();
                                            switch (typeLower) {
                                                case "subscription_created":
                                                    return t(
                                                        "subscription_created"
                                                    );
                                                case "subscription_cancelled":
                                                    return t(
                                                        "subscription_cancelled"
                                                    );
                                                case "plan_change":
                                                    return t("plan_change");
                                                case "status_update":
                                                    return t("status_update");
                                                case "admin_action":
                                                    return t("admin_action");
                                                case "auto_free_subscription_created":
                                                    return t(
                                                        "auto_free_subscription_created"
                                                    );
                                                case "payment":
                                                    return t("payment");
                                                case "refund":
                                                    return t("refund");
                                                case "partial_refund":
                                                    return t("partial_refund");
                                                default:
                                                    return (
                                                        rowData.type ||
                                                        t("unknown")
                                                    );
                                            }
                                        },
                                        cellStyle: { fontSize: "0.8rem" },
                                    },
                                    {
                                        title: t("amount"),
                                        field: "amount",
                                        render: (rowData) =>
                                            formatPrice(rowData.amount),
                                        cellStyle: { fontSize: "0.8rem" },
                                    },
                                    {
                                        title: t("status"),
                                        field: "status",
                                        render: (transactionRowData) => {
                                            // Get the actual current status based on endDate
                                            // Use the main subscriber data (rowData) for checking expiration
                                            const actualStatus =
                                                getActualStatus(
                                                    transactionRowData.status,
                                                    rowData
                                                );

                                            return (
                                                <Chip
                                                    label={formatStatus(
                                                        actualStatus,
                                                        t
                                                    )}
                                                    style={{
                                                        backgroundColor:
                                                            getStatusBackgroundColor(
                                                                actualStatus
                                                            ),
                                                        color: getStatusTextColor(
                                                            actualStatus
                                                        ),
                                                        fontSize: "0.7rem",
                                                        fontWeight: "bold",
                                                    }}
                                                    size="small"
                                                />
                                            );
                                        },
                                        cellStyle: { fontSize: "0.8rem" },
                                    },
                                    {
                                        title: t("payment_method"),
                                        field: "paymentMethod",
                                        render: (rowData) =>
                                            formatPaymentMethod(
                                                rowData.paymentMethod,
                                                t
                                            ),
                                        cellStyle: { fontSize: "0.8rem" },
                                    },
                                    {
                                        title: t("admin_note"),
                                        field: "adminNote",
                                        render: (rowData) =>
                                            formatAdminNote(
                                                rowData.adminNote,
                                                t
                                            ),
                                        cellStyle: { fontSize: "0.8rem" },
                                    },
                                ]}
                                data={rowData.transactions || []}
                                options={{
                                    search: false,
                                    paging: false,
                                    toolbar: false,
                                    showTitle: false,
                                    rowStyle: {
                                        fontSize: "0.8rem",
                                    },
                                    headerStyle: {
                                        fontWeight: "bold",
                                        backgroundColor: "#f9f9f9",
                                        fontSize: "0.8rem",
                                    },
                                }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {rowData.status === "pending" &&
                    rowData.paymentMethod === "offline" && (
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography className={classes.detailTitle}>
                                        {t("payment_verification")}
                                    </Typography>
                                    <Box display="flex" gap={2} mt={1}>
                                        <Button
                                            size="small"
                                            onClick={() =>
                                                handleApprovePayment(rowData)
                                            }
                                            className={classes.approveButton}
                                            startIcon={<CheckIcon />}
                                        >
                                            {t("approve_payment")}
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() =>
                                                handleRejectPayment(rowData)
                                            }
                                            className={classes.rejectButton}
                                            startIcon={<BlockIcon />}
                                        >
                                            {t("reject_payment")}
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
            </Grid>
        </div>
    );

    if (loading) {
        return (
            <div className={classes.loadingContainer}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <Typography variant="h5" style={{ fontWeight: "bold" }}>
                    {t("subscription_subscribers")}
                </Typography>
                <Button
                    color="primary"
                    startIcon={<GetAppIcon />}
                    onClick={handleExport}
                >
                    {t("export_csv")}
                </Button>
            </Box>

            {error && (
                <Alert severity="error" className={classes.errorContainer}>
                    {error}
                </Alert>
            )}

            {/* Summary Card */}
            <Card className={classes.summaryCard}>
                <Typography className={classes.summaryText}>
                    {t("showing_subscribers")}: {filteredSubscribers.length}{" "}
                    {t("total")}
                </Typography>
            </Card>

            {/* Filters */}
            <div className={classes.filterContainer}>
                <FormControl
                    className={`${classes.filterItem} ${classes.formControl}`}
                    size="small"
                >
                    <InputLabel>{t("filter_by_plan")}</InputLabel>
                    <Select
                        value={filterPlan}
                        onChange={(e) => setFilterPlan(e.target.value)}
                        label={t("filter_by_plan")}
                        disabled={plansLoading}
                    >
                        <MenuItem value="">{t("all_plans")}</MenuItem>
                        {plansLoading ? (
                            <MenuItem disabled>
                                <CircularProgress
                                    size={16}
                                    style={{ marginRight: 8 }}
                                />
                                {t("loading")}
                            </MenuItem>
                        ) : plansError ? (
                            <MenuItem disabled>{plansError}</MenuItem>
                        ) : allPlans.length > 0 ? (
                            allPlans
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((plan) => (
                                    <MenuItem key={plan.id} value={plan.name}>
                                        {plan.name}
                                    </MenuItem>
                                ))
                        ) : (
                            <MenuItem disabled>No plans available</MenuItem>
                        )}
                    </Select>
                </FormControl>

                <FormControl
                    className={`${classes.filterItem} ${classes.formControl}`}
                    size="small"
                >
                    <InputLabel>{t("filter_by_status")}</InputLabel>
                    <Select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        label={t("filter_by_status")}
                    >
                        <MenuItem value="">{t("all_status")}</MenuItem>
                        <MenuItem value="active">{t("active")}</MenuItem>
                        <MenuItem value="pending">{t("pending")}</MenuItem>
                        <MenuItem value="expired">{t("expired")}</MenuItem>
                        <MenuItem value="cancelled">{t("cancelled")}</MenuItem>
                        <MenuItem value="payment_pending">
                            {t("payment_pending")}
                        </MenuItem>
                    </Select>
                </FormControl>

                <FormControl
                    className={`${classes.filterItem} ${classes.formControl}`}
                    size="small"
                >
                    <InputLabel>{t("filter_by_user_type")}</InputLabel>
                    <Select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        label={t("filter_by_user_type")}
                    >
                        <MenuItem value="">{t("all_user_types")}</MenuItem>
                        <MenuItem value="customer">{t("customer")}</MenuItem>
                        <MenuItem value="driver">{t("driver")}</MenuItem>
                    </Select>
                </FormControl>
            </div>

            {/* MaterialTable with expandable rows */}
            <div className={classes.materialTable}>
                <MaterialTable
                    title=""
                    columns={[
                        {
                            title: t("subscriber_name"),
                            field: "name",
                            render: (rowData) => (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Avatar style={{ width: 32, height: 32 }}>
                                        {rowData.name?.charAt(0) || "U"}
                                    </Avatar>
                                    <div>
                                        <Typography
                                            variant="body2"
                                            style={{ fontWeight: "bold" }}
                                        >
                                            {rowData.name &&
                                            rowData.name !== "Unknown User"
                                                ? rowData.name
                                                : t("unknown_user")}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            {rowData.email &&
                                            rowData.email.trim() !== ""
                                                ? rowData.email
                                                : t("no_email")}
                                        </Typography>
                                    </div>
                                </Box>
                            ),
                        },
                        {
                            title: t("plan"),
                            field: "planName",
                            render: (rowData) => (
                                <div>
                                    <Typography variant="body2">
                                        {formatPlanName(rowData.planName)}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        {formatPrice(rowData.price)} /{" "}
                                        {t(rowData.period || "monthly")}
                                    </Typography>
                                </div>
                            ),
                        },
                        {
                            title: t("status"),
                            field: "status",
                            render: (rowData) => {
                                // Calculate endDate if not available for time remaining
                                let displayEndDate = rowData.endDate;
                                let isCalculated = false;

                                if (
                                    !displayEndDate &&
                                    rowData.startDate &&
                                    rowData.period
                                ) {
                                    displayEndDate = calculateEndDateFromPeriod(
                                        rowData.startDate,
                                        rowData.period
                                    );
                                    isCalculated = true;
                                }

                                const timeRemaining =
                                    formatTimeRemaining(displayEndDate);
                                const isExpired = timeRemaining.isExpired;

                                return (
                                    <Box>
                                        <Chip
                                            label={formatStatus(
                                                rowData.status,
                                                t
                                            )}
                                            style={{
                                                backgroundColor:
                                                    getStatusBackgroundColor(
                                                        rowData.status
                                                    ),
                                                color: getStatusTextColor(
                                                    rowData.status
                                                ),
                                                fontSize: "0.75rem",
                                                fontWeight: "bold",
                                                marginBottom: 4,
                                            }}
                                            size="small"
                                        />
                                        {/* Only show time remaining for active subscriptions */}
                                        {displayEndDate &&
                                            rowData.status === "active" && (
                                                <Typography
                                                    variant="caption"
                                                    style={{
                                                        color: timeRemaining.color,
                                                        fontWeight: "bold",
                                                        display: "block",
                                                    }}
                                                >
                                                    {timeRemaining.text}
                                                    {isCalculated && (
                                                        <span
                                                            style={{
                                                                color: "#FF9800",
                                                                marginLeft: 4,
                                                            }}
                                                        >
                                                            ({t("calculated")})
                                                        </span>
                                                    )}
                                                </Typography>
                                            )}
                                        {/* Show appropriate message for cancelled subscriptions */}
                                        {rowData.status === "cancelled" && (
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    color: "#f44336",
                                                    fontWeight: "bold",
                                                    display: "block",
                                                }}
                                            >
                                                {t("subscription_cancelled")}
                                            </Typography>
                                        )}
                                        {/* Show appropriate message for expired subscriptions */}
                                        {rowData.status === "expired" && (
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    color: "#f44336",
                                                    fontWeight: "bold",
                                                    display: "block",
                                                }}
                                            >
                                                {t("subscription_expired")}
                                            </Typography>
                                        )}
                                        {/* Show appropriate message for payment pending */}
                                        {rowData.status ===
                                            "payment_pending" && (
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    color: "#FF9800",
                                                    fontWeight: "bold",
                                                    display: "block",
                                                }}
                                            >
                                                {t("payment_pending")}
                                            </Typography>
                                        )}
                                        {/* Show appropriate message for pending subscriptions */}
                                        {rowData.status === "pending" && (
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    color: "#FF9800",
                                                    fontWeight: "bold",
                                                    display: "block",
                                                }}
                                            >
                                                {t("pending")}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            },
                        },
                        {
                            title: t("start_date"),
                            field: "startDate",
                            render: (rowData) => formatDate(rowData.startDate),
                        },
                        {
                            title: t("end_date"),
                            field: "endDate",
                            render: (rowData) => {
                                // Calculate endDate if not available
                                let displayEndDate = rowData.endDate;
                                let isCalculated = false;

                                if (
                                    !displayEndDate &&
                                    rowData.startDate &&
                                    rowData.period
                                ) {
                                    displayEndDate = calculateEndDateFromPeriod(
                                        rowData.startDate,
                                        rowData.period
                                    );
                                    isCalculated = true;
                                }

                                return (
                                    <Box>
                                        <Typography variant="body2">
                                            {formatExpiryDate(displayEndDate)}
                                        </Typography>
                                        {displayEndDate && (
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                style={{ display: "block" }}
                                            >
                                                {t("period")}:{" "}
                                                {t(rowData.period || "monthly")}
                                                {isCalculated && (
                                                    <span
                                                        style={{
                                                            color: "#FF9800",
                                                            marginLeft: 4,
                                                        }}
                                                    >
                                                        ({t("calculated")})
                                                    </span>
                                                )}
                                            </Typography>
                                        )}
                                        {!displayEndDate && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                                style={{ display: "block" }}
                                            >
                                                {t("no_end_date")}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            },
                        },
                        {
                            title: t("actions"),
                            field: "actions",
                            render: (rowData) => (
                                <Box>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            handleEditSubscriber(rowData)
                                        }
                                        className={classes.actionButton}
                                        title={t("edit_subscription")}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            handleChangePlan(rowData)
                                        }
                                        className={classes.actionButton}
                                        title={t("change_plan")}
                                    >
                                        <SwapHorizIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            handleCancelSubscription(rowData)
                                        }
                                        className={classes.actionButton}
                                        title={t("cancel_subscription")}
                                        disabled={
                                            rowData.status === "cancelled" ||
                                            rowData.status === "expired"
                                        }
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            handleViewHistory(rowData)
                                        }
                                        className={classes.actionButton}
                                        title={t("view_history")}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ),
                            sorting: false,
                        },
                    ]}
                    data={filteredSubscribers}
                    detailPanel={(rowData) => renderExpandedRow(rowData)}
                    options={{
                        pageSize: 10,
                        pageSizeOptions: [5, 10, 20, 50],
                        search: true,
                        sorting: true,
                        exportButton: false,
                        actionsColumnIndex: -1,
                        rowStyle: {
                            fontSize: 14,
                        },
                        headerStyle: {
                            fontWeight: "bold",
                            backgroundColor: "#f5f5f5",
                        },
                    }}
                    localization={{
                        pagination: {
                            labelDisplayedRows: `{from}-{to} ${t(
                                "of"
                            )} {count}`,
                            labelRowsSelect: t("rows"),
                            labelRowsPerPage: `${t("rows_per_page")}:`,
                            firstAriaLabel: t("first_page"),
                            firstTooltip: t("first_page"),
                            previousAriaLabel: t("previous_page"),
                            previousTooltip: t("previous_page"),
                            nextAriaLabel: t("next_page"),
                            nextTooltip: t("next_page"),
                            lastAriaLabel: t("last_page"),
                            lastTooltip: t("last_page"),
                        },
                        toolbar: {
                            searchTooltip: t("search"),
                            searchPlaceholder: t("search_subscribers"),
                        },
                        header: {
                            actions: t("actions"),
                        },
                        body: {
                            emptyDataSourceMessage: t("no_subscribers_found"),
                        },
                    }}
                />
            </div>

            {/* Edit Subscriber Modal */}
            <Modal
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                className={classes.modal}
            >
                <div className={classes.modalPaper}>
                    <div className={classes.modalHeader}>
                        <Typography variant="h6">
                            {t("edit_subscriber")}
                        </Typography>
                        <IconButton onClick={() => setEditModalOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div className={classes.modalContent}>
                        {selectedSubscriber && (
                            <>
                                <Typography
                                    variant="body1"
                                    style={{ marginBottom: 16 }}
                                >
                                    <strong>{t("subscriber_name")}:</strong>{" "}
                                    {selectedSubscriber.name}
                                </Typography>

                                <FormControl
                                    fullWidth
                                    className={classes.formControl}
                                    style={{ marginBottom: 16 }}
                                >
                                    <InputLabel>{t("status")}</InputLabel>
                                    <Select
                                        value={editFormData.status || ""}
                                        onChange={(e) =>
                                            setEditFormData((prev) => ({
                                                ...prev,
                                                status: e.target.value,
                                            }))
                                        }
                                    >
                                        <MenuItem value="active">
                                            {t("active")}
                                        </MenuItem>
                                        <MenuItem value="pending">
                                            {t("pending")}
                                        </MenuItem>
                                        <MenuItem value="expired">
                                            {t("expired")}
                                        </MenuItem>
                                        <MenuItem value="cancelled">
                                            {t("cancelled")}
                                        </MenuItem>
                                        <MenuItem value="suspended">
                                            {t("suspended")}
                                        </MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    className={classes.formControl}
                                    label={t("admin_note")}
                                    multiline
                                    rows={3}
                                    value={editFormData.adminNote || ""}
                                    onChange={(e) =>
                                        setEditFormData((prev) => ({
                                            ...prev,
                                            adminNote: e.target.value,
                                        }))
                                    }
                                    placeholder={t("enter_admin_note")}
                                />
                            </>
                        )}
                    </div>

                    <div className={classes.modalFooter}>
                        <MuiButton onClick={() => setEditModalOpen(false)}>
                            {t("cancel")}
                        </MuiButton>
                        <Button color="primary" onClick={handleSaveEdit}>
                            {t("save_changes")}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Change Plan Modal */}
            <Modal
                open={changePlanModalOpen}
                onClose={() => setChangePlanModalOpen(false)}
                className={classes.modal}
            >
                <div className={classes.modalPaper}>
                    <div className={classes.modalHeader}>
                        <Typography variant="h6">
                            {t("change_subscription_plan")}
                        </Typography>
                        <IconButton
                            onClick={() => setChangePlanModalOpen(false)}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div className={classes.modalContent}>
                        {selectedSubscriber && (
                            <>
                                <Typography
                                    variant="body1"
                                    style={{ marginBottom: 16 }}
                                >
                                    <strong>{t("current_plan")}:</strong>{" "}
                                    {selectedSubscriber.planName} (
                                    {formatPrice(selectedSubscriber.price)})
                                </Typography>

                                <FormControl
                                    fullWidth
                                    className={classes.formControl}
                                    style={{ marginBottom: 16 }}
                                >
                                    <InputLabel>
                                        {t("select_new_plan")}
                                    </InputLabel>
                                    <Select
                                        value={selectedNewPlan?.id || ""}
                                        onChange={(e) => {
                                            const plan = availablePlans.find(
                                                (p) => p.id === e.target.value
                                            );
                                            setSelectedNewPlan(plan);
                                        }}
                                    >
                                        {availablePlans.map((plan) => (
                                            <MenuItem
                                                key={plan.id}
                                                value={plan.id}
                                            >
                                                {plan.translations?.en?.name ||
                                                    plan.name}{" "}
                                                - {formatPrice(plan.price)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {selectedNewPlan && (
                                    <Typography
                                        variant="body2"
                                        style={{
                                            marginBottom: 16,
                                            color: "#666",
                                        }}
                                    >
                                        <strong>{t("plan_details")}:</strong>{" "}
                                        {selectedNewPlan.description ||
                                            selectedNewPlan.translations?.en
                                                ?.description ||
                                            t("no_description")}
                                    </Typography>
                                )}
                            </>
                        )}
                    </div>

                    <div className={classes.modalFooter}>
                        <MuiButton
                            onClick={() => setChangePlanModalOpen(false)}
                        >
                            {t("cancel")}
                        </MuiButton>
                        <Button
                            color="primary"
                            onClick={handleConfirmPlanChange}
                        >
                            {t("change_plan")}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Cancel Subscription Modal */}
            <Modal
                open={cancelModalOpen}
                onClose={() => setCancelModalOpen(false)}
                className={classes.modal}
            >
                <div className={classes.modalPaper}>
                    <div className={classes.modalHeader}>
                        <Typography variant="h6">
                            {t("cancel_subscription")}
                        </Typography>
                        <IconButton onClick={() => setCancelModalOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div className={classes.modalContent}>
                        {selectedSubscriber && (
                            <>
                                <Typography
                                    variant="body1"
                                    style={{ marginBottom: 16 }}
                                >
                                    <strong>{t("subscriber")}:</strong>{" "}
                                    {selectedSubscriber.name}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    style={{ marginBottom: 16 }}
                                >
                                    <strong>{t("current_plan")}:</strong>{" "}
                                    {selectedSubscriber.planName}
                                </Typography>

                                <TextField
                                    fullWidth
                                    className={classes.formControl}
                                    label={t("cancel_reason")}
                                    multiline
                                    rows={3}
                                    value={cancelReason}
                                    onChange={(e) =>
                                        setCancelReason(e.target.value)
                                    }
                                    placeholder={t("enter_cancel_reason")}
                                    required
                                />
                            </>
                        )}
                    </div>

                    <div className={classes.modalFooter}>
                        <MuiButton onClick={() => setCancelModalOpen(false)}>
                            {t("cancel")}
                        </MuiButton>
                        <Button color="danger" onClick={handleConfirmCancel}>
                            {t("confirm_cancel")}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Subscription History Modal */}
            <Modal
                open={historyModalOpen}
                onClose={() => setHistoryModalOpen(false)}
                className={classes.modal}
            >
                <div className={classes.modalPaper}>
                    <div className={classes.modalHeader}>
                        <Typography variant="h6">
                            {t("subscription_history")}
                        </Typography>
                        <IconButton onClick={() => setHistoryModalOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div className={classes.modalContent}>
                        {selectedSubscriber && (
                            <>
                                <Typography
                                    variant="body1"
                                    style={{ marginBottom: 16 }}
                                >
                                    <strong>{t("subscriber")}:</strong>{" "}
                                    {selectedSubscriber.name}
                                </Typography>

                                <MaterialTable
                                    title=""
                                    columns={[
                                        {
                                            title: t("date"),
                                            field: "timestamp",
                                            render: (rowData) =>
                                                formatDate(rowData.timestamp),
                                            cellStyle: { fontSize: "0.8rem" },
                                        },
                                        {
                                            title: t("action"),
                                            field: "type",
                                            render: (rowData) => {
                                                const actionText =
                                                    t(rowData.type) ||
                                                    rowData.type;
                                                return (
                                                    <div>
                                                        <div
                                                            style={{
                                                                fontWeight:
                                                                    "bold",
                                                            }}
                                                        >
                                                            {actionText}
                                                        </div>
                                                        {rowData.planName && (
                                                            <div
                                                                style={{
                                                                    fontSize:
                                                                        "0.7rem",
                                                                    color: "#666",
                                                                }}
                                                            >
                                                                {
                                                                    rowData.planName
                                                                }
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            },
                                            cellStyle: { fontSize: "0.8rem" },
                                        },
                                        {
                                            title: t("details"),
                                            field: "note",
                                            render: (rowData) => {
                                                const details = [];
                                                if (rowData.amount) {
                                                    details.push(
                                                        `$${parseFloat(
                                                            rowData.amount
                                                        ).toFixed(2)}`
                                                    );
                                                }
                                                if (rowData.paymentMethod) {
                                                    details.push(
                                                        t(rowData.paymentMethod)
                                                    );
                                                }
                                                if (
                                                    rowData.note &&
                                                    rowData.note !== "-" &&
                                                    rowData.note !== ""
                                                ) {
                                                    details.push(
                                                        formatAdminNote(
                                                            rowData.note,
                                                            t
                                                        )
                                                    );
                                                }
                                                return details.length > 0
                                                    ? details.join(" • ")
                                                    : "-";
                                            },
                                            cellStyle: { fontSize: "0.8rem" },
                                        },
                                        {
                                            title: t("status"),
                                            field: "status",
                                            render: (transactionRowData) => {
                                                // Get the actual current status based on endDate
                                                // Use selectedSubscriber for checking expiration in modal
                                                const actualStatus =
                                                    getActualStatus(
                                                        transactionRowData.status,
                                                        selectedSubscriber
                                                    );

                                                return (
                                                    <Chip
                                                        label={formatStatus(
                                                            actualStatus,
                                                            t
                                                        )}
                                                        style={{
                                                            backgroundColor:
                                                                getStatusBackgroundColor(
                                                                    actualStatus
                                                                ),
                                                            color: getStatusTextColor(
                                                                actualStatus
                                                            ),
                                                            fontSize: "0.7rem",
                                                            fontWeight: "bold",
                                                        }}
                                                        size="small"
                                                    />
                                                );
                                            },
                                            cellStyle: { fontSize: "0.8rem" },
                                        },
                                    ]}
                                    data={selectedSubscriber.transactions || []}
                                    options={{
                                        search: false,
                                        paging: true,
                                        pageSize: 10,
                                        toolbar: false,
                                        showTitle: false,
                                        rowStyle: {
                                            fontSize: "0.8rem",
                                        },
                                        headerStyle: {
                                            fontWeight: "bold",
                                            backgroundColor: "#f9f9f9",
                                            fontSize: "0.8rem",
                                        },
                                    }}
                                    localization={{
                                        pagination: {
                                            labelDisplayedRows: `{from}-{to} ${t(
                                                "of"
                                            )} {count}`,
                                            labelRowsSelect: t("rows"),
                                            labelRowsPerPage: `${t(
                                                "rows_per_page"
                                            )}:`,
                                            firstAriaLabel: t("first_page"),
                                            firstTooltip: t("first_page"),
                                            previousAriaLabel:
                                                t("previous_page"),
                                            previousTooltip: t("previous_page"),
                                            nextAriaLabel: t("next_page"),
                                            nextTooltip: t("next_page"),
                                            lastAriaLabel: t("last_page"),
                                            lastTooltip: t("last_page"),
                                        },
                                        toolbar: {
                                            searchTooltip: t("search"),
                                            searchPlaceholder:
                                                t("search_subscribers"),
                                        },
                                        header: {
                                            actions: t("actions"),
                                        },
                                        body: {
                                            emptyDataSourceMessage: t(
                                                "no_subscribers_found"
                                            ),
                                        },
                                    }}
                                />
                            </>
                        )}
                    </div>

                    <div className={classes.modalFooter}>
                        <MuiButton onClick={() => setHistoryModalOpen(false)}>
                            {t("close")}
                        </MuiButton>
                    </div>
                </div>
            </Modal>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}
